import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import {
  readBoolean,
  readString,
  readStringArray,
  readStringEnum,
} from "./actionArgumentReaders"

export function parseReviewActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const problems: string[] = []
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only Human Review actions.
  switch (action.kind) {
    case "create_review_project": {
      const display_name = readString(action.args, "display_name")
      const assignees = readStringArray(action.args, "assignees")
      if (!display_name) {
        problems.push("missing display_name")
      }
      if (!assignees || assignees.length === 0) {
        problems.push("missing assignees")
      }
      if (problems.length > 0 || !display_name || !assignees) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "create_review_project",
          args: {
            display_name,
            assignees,
            description: readString(action.args, "description"),
            linked_simulation_ids: readStringArray(
              action.args,
              "linked_simulation_ids",
            ),
            linked_metric_ids: readStringArray(
              action.args,
              "linked_metric_ids",
            ),
            project_type: readStringEnum(action.args, "project_type", [
              "PROJECT_INDIVIDUAL",
              "PROJECT_COLLABORATIVE",
            ] as const),
            notifications: readBoolean(action.args, "notifications"),
            project_rules: readStringArray(action.args, "project_rules"),
            review_label_options: readStringArray(
              action.args,
              "review_label_options",
            ),
            review_label_input_mode: readStringEnum(
              action.args,
              "review_label_input_mode",
              ["OPTION_ONLY", "CUSTOM_ONLY", "OPTION_OR_CUSTOM"] as const,
            ),
            review_label_selection_mode: readStringEnum(
              action.args,
              "review_label_selection_mode",
              ["SINGLE", "MULTIPLE"] as const,
            ),
          },
        },
      }
    }
    case "add_run_to_review_project": {
      const project_id = readString(action.args, "project_id")
      const run_id = readString(action.args, "run_id")
      if (!project_id || !run_id) {
        return {
          ok: false,
          problems: [
            ...(!project_id ? ["missing project_id"] : []),
            ...(!run_id ? ["missing run_id"] : []),
          ],
        }
      }
      return {
        ok: true,
        action: {
          kind: "add_run_to_review_project",
          args: { project_id, run_id },
        },
      }
    }
    case "add_simulations_to_review_project":
    case "remove_simulations_from_review_project": {
      const project_id = readString(action.args, "project_id")
      const simulation_output_ids = readStringArray(
        action.args,
        "simulation_output_ids",
      )
      if (!project_id || !simulation_output_ids?.length) {
        return {
          ok: false,
          problems: [
            ...(!project_id ? ["missing project_id"] : []),
            ...(!simulation_output_ids?.length
              ? ["missing simulation_output_ids"]
              : []),
          ],
        }
      }
      return {
        ok: true,
        action: {
          kind: action.kind,
          args: { project_id, simulation_output_ids },
        },
      }
    }
    // Modify (update_*) and destructive (delete_*) kinds. The id is the
    // only hard requirement; change fields are optional and lenient (Sofia
    // validates the full set server-side). Execution runs through the signed confirmation echo.
    case "update_review_project": {
      const project_id = readString(action.args, "project_id")
      if (!project_id) {
        return { ok: false, problems: ["missing project_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_review_project",
          args: {
            project_id,
            display_name: readString(action.args, "display_name"),
            description: readString(action.args, "description"),
            assignees: readStringArray(action.args, "assignees"),
            linked_metric_ids: readStringArray(
              action.args,
              "linked_metric_ids",
            ),
            project_type: readStringEnum(action.args, "project_type", [
              "PROJECT_INDIVIDUAL",
              "PROJECT_COLLABORATIVE",
            ] as const),
            notifications: readBoolean(action.args, "notifications"),
            project_rules: readStringArray(action.args, "project_rules"),
            review_label_options: readStringArray(
              action.args,
              "review_label_options",
            ),
            review_label_input_mode: readStringEnum(
              action.args,
              "review_label_input_mode",
              ["OPTION_ONLY", "CUSTOM_ONLY", "OPTION_OR_CUSTOM"] as const,
            ),
            review_label_selection_mode: readStringEnum(
              action.args,
              "review_label_selection_mode",
              ["SINGLE", "MULTIPLE"] as const,
            ),
          },
        },
      }
    }
    case "delete_review_project": {
      const project_id = readString(action.args, "project_id")
      if (!project_id) {
        return { ok: false, problems: ["missing project_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_review_project", args: { project_id } },
      }
    }
    default:
      return undefined
  }
}
